import { DateOnly, subMonths } from '@if/core/date'

// The history's period filter, as the Monolith offers it: three rolling windows, then one entry per
// year that has at least one deposit. A rolling window has no upper bound, so a file deposited ahead
// of today still shows.
export const ROLLING_PERIODS = ['1m', '3m', '6m'] as const
export type RollingPeriod = (typeof ROLLING_PERIODS)[number]

/** A rolling window, or a calendar year written as its four digits. */
export type DisbursementHistoryPeriod = RollingPeriod | `${number}`

export const DEFAULT_PERIOD: DisbursementHistoryPeriod = '1m'

const MONTHS_BY_ROLLING_PERIOD: Record<RollingPeriod, number> = { '1m': 1, '3m': 3, '6m': 6 }

export const isRollingPeriod = (value: string): value is RollingPeriod =>
  (ROLLING_PERIODS as readonly string[]).includes(value)

const YEAR_PATTERN = /^\d{4}$/

/** The period a URL carries, or the default when it carries none the filter offers. */
export const toPeriod = (value: string | null): DisbursementHistoryPeriod =>
  value !== null && (isRollingPeriod(value) || YEAR_PATTERN.test(value))
    ? (value as DisbursementHistoryPeriod)
    : DEFAULT_PERIOD

export interface PeriodRange {
  from: DateOnly
  to: DateOnly | null
}

/** Both bounds are inclusive, on the file's deposit date. */
export const periodRange = (period: DisbursementHistoryPeriod, today: DateOnly): PeriodRange => {
  if (isRollingPeriod(period)) {
    return { from: DateOnly.fromDate(subMonths(today, MONTHS_BY_ROLLING_PERIOD[period])), to: null }
  }
  const year = Number(period)
  return { from: DateOnly.fromComponents(year, 0, 1), to: DateOnly.fromComponents(year, 11, 31) }
}

/** The rolling windows first, then the years with files, newest first. */
export const periodOptions = (years: readonly number[]): DisbursementHistoryPeriod[] => [
  ...ROLLING_PERIODS,
  ...[...years].sort((a, b) => b - a).map((year): DisbursementHistoryPeriod => `${year}`),
]
